import { computed, reactive } from 'vue'
import demoLogoUrl from '../assets/northstar-logo.png'
import { normalizeSessionBranding } from '../branding/sessionBranding.js'
import { localeTag } from '../i18n.js'
import { pollResultsPublished } from '../polls/publication.js'
import { applyLibrarySnapshot, emptyActivePoll } from '../polls/pollLibrary.js'
import { clearOwnPollResponses, readPollResponse, rememberCurrentPollResponse, rememberPollResponse } from '../polls/ownPollResponses.js'
import { participantNameRequired, questionIdentityMode } from '../auth/questionIdentity.js'

// Demo-only server-side aggregate stand-in. Never hydrate hidden answers into
// the public cloud; publication retrieves the saved aggregate just as the API does.
const demoCloudResults = new Map()
const demoCloudKey = pollId => JSON.stringify([state.session.code, pollId])

const now = Date.now()
const includeDemoData = import.meta.env.VITE_DEMO_MODE === 'true' || import.meta.env.MODE === 'test'

const seedQuestions = [
  { id: 'q-1048', text: 'How do we keep the human connection as more of our work becomes automated?', author: 'Maya Chen', participantId: 'p-maya', isOwn: false, votes: 184, status: 'current', createdAt: now - 22 * 60_000, edited: false, voted: false },
  { id: 'q-1042', text: 'What is one leadership habit you had to unlearn as your organization grew?', author: 'Anonymous', participantId: 'p-guest-44', votes: 156, status: 'queued', createdAt: now - 31 * 60_000, edited: false, voted: true },
  { id: 'q-1051', text: 'Where should leaders draw the line between transparency and creating unnecessary anxiety?', author: 'Julien Moreau', participantId: 'p-julien', votes: 129, status: 'queued', createdAt: now - 14 * 60_000, edited: false, voted: false },
  { id: 'q-1057', text: 'How can a small team make room for experimentation when every deadline already feels urgent?', author: 'Anonymous', participantId: 'p-guest-63', votes: 88, status: 'approved', createdAt: now - 8 * 60_000, edited: false, voted: false },
  { id: 'q-1055', text: 'Could you share a time when listening changed a decision you were certain about?', author: 'Priya S.', participantId: 'p-priya', votes: 74, status: 'approved', createdAt: now - 11 * 60_000, edited: false, voted: false },
  { id: 'q-1060', text: 'What metrics tell you whether a culture is actually healthy—not just productive?', author: 'Anonymous', participantId: 'demo-participant', isOwn: true, votes: 41, status: 'submitted', createdAt: now - 3 * 60_000, edited: false, voted: false },
  { id: 'q-1037', text: 'What is the most underrated skill for a first-time people manager?', author: 'Eli Thompson', participantId: 'p-eli', votes: 117, status: 'answered', createdAt: now - 46 * 60_000, edited: false, voted: false },
  { id: 'q-1062', text: 'Do you expect hybrid work policies to settle, or keep shifting every few years?', author: 'Sofia R.', participantId: 'p-sofia', votes: 19, status: 'submitted', createdAt: now - 90_000, edited: false, voted: false },
]

const initialState = () => ({
  connection: 'connected',
  accessBlocked: false,
  participantMuted: false,
  revision: 42,
  participantId: includeDemoData ? (localStorage.getItem('gather-participant') || 'demo-participant') : null,
  participantToken: null,
  audienceJoined: false,
  audienceEntryGeneration: 0,
  participantCode: null,
  participantIdentity: { name: '', email: '', thirdPartyId: '' },
  moderatorAuthenticated: false,
  moderatorToken: null,
  moderatorScopes: null,
  pollLibrary: [],
  pollLibraryLoaded: false,
  activePollRevision: 0,
  presenceUpdatedAt: 0,
  presenceStatus: includeDemoData ? 'demo' : 'pending',
  presenceReason: null,
  participantPage: { pageSize: 25, continuationToken: null, currentToken: null },
  speakerToken: null,
  activeSpeakerId: null,
  adminAuthenticated: import.meta.env.VITE_DEMO_MODE === 'true',
  customer: {
    id: includeDemoData ? 'northstar' : (import.meta.env.VITE_CUSTOMER_ID || ''),
    name: includeDemoData ? 'Northstar Group' : (import.meta.env.VITE_CUSTOMER_NAME || 'Gather workspace'),
    region: includeDemoData ? 'Canada Central' : (import.meta.env.VITE_CUSTOMER_REGION || 'Region not configured'),
  },
  platformCapacity: {
    desiredUnits: includeDemoData ? 20 : 0,
    actualUnits: includeDemoData ? 20 : 0,
    maximumUnits: 100,
    state: includeDemoData ? 'Applied' : 'Released',
    expiresAt: null,
    lastChangedBy: includeDemoData ? 'elias@example.com' : null,
    updatedAt: includeDemoData ? new Date(now - 45 * 60_000).toISOString() : null,
    etag: includeDemoData ? 'demo-capacity-1' : null,
  },
  session: {
    id: includeDemoData ? 'ses-northstar-2026' : '',
    customerId: includeDemoData ? 'northstar' : '',
    code: includeDemoData ? 'EVENT1234' : '',
    name: includeDemoData ? 'Northstar Leadership Forum' : 'Loading session…',
    subtitle: '',
    room: includeDemoData ? 'Main stage' : '',
    startsAt: includeDemoData ? '2026-09-03T13:00:00-04:00' : null,
    endsAt: includeDemoData ? '2026-09-03T18:00:00-04:00' : null,
    status: includeDemoData ? 'open' : 'draft',
    questionsOpen: includeDemoData,
    audienceMode: 'view-and-vote',
    questionIdentityMode: 'Optional',
    requireName: false,
    requireEmail: false,
    questionSlowModeSeconds: includeDemoData ? 30 : 0,
    questionMaxLength: 2000,
    rapidVoteLimit: 6,
    rapidVoteWindowSeconds: 1,
    rapidVoteCooldownSeconds: 15,
    publicQuestionLimit: 200,
    expectedPeak: includeDemoData ? 10000 : 0,
    connectedCount: includeDemoData ? 9842 : null,
    locale: 'en',
    theme: {},
    branding: includeDemoData ? {
      clientName: 'Northstar Group',
      logoAltText: 'Northstar Group compass-star logo',
      showOnStage: true,
      logoUrl: demoLogoUrl,
      version: 'demo-built-in',
      contentType: 'image/png',
      bytes: 28770,
      width: 192,
      height: 192,
    } : normalizeSessionBranding(),
  },
  lastQuestionAt: 0,
  nextQuestionAllowedAt: 0,
  voteEvents: [],
  voteThrottledUntil: 0,
  questions: includeDemoData ? seedQuestions.map((question) => ({ ...question })) : [],
  poll: {
    id: includeDemoData ? 'poll-12' : '',
    title: includeDemoData ? 'What would make the biggest difference to your team this year?' : '',
    status: 'open',
    multiple: false,
    resultsVisible: true,
    resultsAvailable: includeDemoData,
    responded: false,
    selectedOptionIds: [],
    totalResponses: includeDemoData ? 6821 : 0,
    options: includeDemoData ? [
      { id: 'po-1', label: 'More focused time', votes: 2368 },
      { id: 'po-2', label: 'Clearer priorities', votes: 2012 },
      { id: 'po-3', label: 'Better tools', votes: 1341 },
      { id: 'po-4', label: 'More coaching', votes: 1100 },
    ] : [],
  },
  participants: includeDemoData ? [
    { id: 'p-maya', name: 'Maya Chen', activity: '2 questions · 4 votes', state: 'active' },
    { id: 'p-guest-44', name: 'Anonymous · 9A2F', activity: '1 question · 18 votes', state: 'active' },
    { id: 'p-guest-63', name: 'Anonymous · 41BC', activity: '3 questions · 11 votes', state: 'muted' },
    { id: 'p-sofia', name: 'Sofia R.', activity: '1 question · 2 votes', state: 'active' },
  ] : [],
  speakers: includeDemoData ? [
    { id: 'sp-1', name: 'Dr. Leila Morgan', handRaised: true, raisedAt: now - 180_000, status: 'waiting' },
    { id: 'sp-2', name: 'Marcus Cole', handRaised: true, raisedAt: now - 90_000, status: 'waiting' },
    { id: 'sp-3', name: 'Anaïs Tremblay', handRaised: false, raisedAt: null, status: 'joined' },
  ] : [],
  currentSpeakerId: null,
  onDeckSpeakerId: null,
  sessions: includeDemoData ? [
    { id: 'ses-northstar-2026', code: 'EVENT1234', name: 'Northstar Leadership Forum', date: 'Sep 3, 2026', status: 'Live', attendees: 9842, questions: 63, messages: 28100000, connectionHours: 38114, storageOperations: 37800, estimatedCost: 29.84 },
    { id: 'ses-atlas-2026', code: 'ATLAS26', name: 'Atlas Partner Summit', date: 'Sep 12, 2026', status: 'Scheduled', attendees: 0, questions: 0, messages: 0, estimatedCost: 0 },
    { id: 'ses-townhall-aug', code: 'TOWN0826', name: 'August Company Town Hall', date: 'Aug 18, 2026', status: 'Closed', attendees: 2241, questions: 97, messages: 3940000, connectionHours: 6443, storageOperations: 12200, estimatedCost: 7.12 },
    { id: 'ses-product-lab', code: 'LAB0612', name: 'Product Lab: Summer', date: 'Jun 12, 2026', status: 'Purged', attendees: 518, questions: null, messages: 840000, connectionHours: 1124, storageOperations: 4100, estimatedCost: 2.08 },
  ] : [],
  usage: {
    isComplete: includeDemoData,
    dataQuality: includeDemoData ? 'demonstration' : 'provisional',
    missingMetrics: includeDemoData ? [] : ['connection-hours', 'function executions', 'bandwidth', 'storage operations'],
    month: includeDemoData ? 'September 2026' : new Intl.DateTimeFormat(localeTag(), { month: 'long', year: 'numeric' }).format(new Date()),
    connectionsNow: includeDemoData ? 9842 : 0,
    connectionHours: includeDemoData ? 38114 : 0,
    messages: includeDemoData ? 28100000 : 0,
    functionExecutions: includeDemoData ? 127400 : 0,
    storageOperations: includeDemoData ? 37800 : 0,
    bandwidthGb: includeDemoData ? 17.4 : 0,
    estimatedMonthCost: includeDemoData ? 43.76 : 0,
    costs: includeDemoData ? { realtime: 28.17, compute: 0.63, storage: 0.09, bandwidth: 0.95 } : { realtime: 0, compute: 0, storage: 0, bandwidth: 0 },
    includedCredits: includeDemoData ? 2500 : 0,
    dailyConnections: includeDemoData ? [720, 840, 1200, 980, 1640, 810, 9842] : [0, 0, 0, 0, 0, 0, 0],
    dailyCosts: includeDemoData ? [
      { label: 'Aug 28', realtime: 2, compute: 1, storage: 1 },
      { label: 'Aug 29', realtime: 3, compute: 1, storage: 1 },
      { label: 'Aug 30', realtime: 4, compute: 2, storage: 1 },
      { label: 'Aug 31', realtime: 2, compute: 1, storage: 1 },
      { label: 'Sep 1', realtime: 5, compute: 2, storage: 1 },
      { label: 'Sep 2', realtime: 3, compute: 1, storage: 1 },
      { label: 'Sep 3', realtime: 86, compute: 8, storage: 6 },
    ] : [],
  },
  exports: includeDemoData ? [
    { id: 'ex-2', format: 'JSON', createdAt: 'Today, 4:42 PM', size: '3.8 MB', status: 'Ready', expires: 'in 23 hours' },
    { id: 'ex-1', format: 'Word', createdAt: 'Today, 4:39 PM', size: '842 KB', status: 'Ready', expires: 'in 23 hours' },
  ] : [],
  apiKeys: includeDemoData ? [
    { id: 'key-live-7f2', name: 'Main stage Companion', prefix: 'ns_live_7f2a••••••••', scopes: ['stage.read', 'stage.control', 'speakers.read'], lastUsed: '12 seconds ago', expires: 'Oct 3, 2026', status: 'Active' },
    { id: 'key-feed-c11', name: 'vMix data feed', prefix: 'ns_live_c11e••••••••', scopes: ['stage.read'], lastUsed: '2 minutes ago', expires: 'Sep 10, 2026', status: 'Active' },
  ] : [],
  webhooks: includeDemoData ? [
    { id: 'wh-1', name: 'Production automation', url: 'https://events.northstar.example/hooks/gather', events: ['stage.changed', 'speaker.selected'], status: 'Healthy', lastDelivery: '12 seconds ago' },
  ] : [],
  moderatorGrants: includeDemoData ? [
    { id: 'grant-avery', initials: 'AM', name: 'Avery · Lead moderator', detail: 'Questions, stage, polls, speakers · expires 7:00 PM', status: 'In use', tone: 'success' },
    { id: 'grant-jordan', initials: 'JM', name: 'Jordan · Question reviewer', detail: 'Questions only · expires 7:00 PM', status: 'Unused', tone: 'neutral' },
    { id: 'grant-av', initials: 'AV', name: 'AV team · Stage control', detail: 'Stage read/control · max 3 uses', status: '2 of 3 uses', tone: 'amber' },
  ] : [],
  toasts: [],
})

export const state = reactive(initialState())

if (includeDemoData && !localStorage.getItem('gather-participant')) {
  localStorage.setItem('gather-participant', state.participantId)
}

export const currentQuestion = computed(() => state.questions.find((q) => q.status === 'current'))
export const queuedQuestions = computed(() => state.questions.filter((q) => q.status === 'queued'))
export const waitingSpeakers = computed(() => state.speakers
  .filter((speaker) => speaker.handRaised)
  .sort((left, right) => (Number(left.queuePosition) || Number.MAX_SAFE_INTEGER)
    - (Number(right.queuePosition) || Number.MAX_SAFE_INTEGER)
    || (Number(left.raisedAt) || Number.MAX_SAFE_INTEGER) - (Number(right.raisedAt) || Number.MAX_SAFE_INTEGER)))

function bumpRevision() {
  state.revision += 1
}

const modeNames = {
  submitownonly: 'own-only',
  'own-only': 'own-only',
    viewquestions: 'view-and-vote',
    'view-all': 'view-and-vote',
  viewandvote: 'view-and-vote',
  'view-and-vote': 'view-and-vote',
}

function timestamp(value, fallback = Date.now()) {
  if (Number.isFinite(value)) return value
  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeQuestion(question, overrides = {}, priorQuestions) {
  const id = question.id || question.questionId
  const local = priorQuestions ? priorQuestions.get(id) : state.questions.find((candidate) => candidate.id === id)
  return {
    id,
    text: question.text || '',
    tags: Array.isArray(question.tags) ? question.tags.filter(tag => typeof tag === 'string') : [],
    author: question.author ?? question.authorName ?? 'Anonymous',
    votes: Number(question.votes ?? question.voteCount ?? 0),
    status: String(question.status || 'submitted').toLowerCase(),
    createdAt: timestamp(question.createdAt),
    updatedAt: timestamp(question.updatedAt),
    edited: question.edited ?? (question.updatedAt && question.createdAt && question.updatedAt !== question.createdAt),
    voted: question.hasVoted ?? question.voted ?? local?.voted ?? false,
    ...(Number.isFinite(Number(question.revision))
      ? { revision: Number(question.revision) }
      : Number.isFinite(Number(local?.revision)) ? { revision: Number(local.revision) } : {}),
    ...(typeof question.isOwn === 'boolean' ? { isOwn: question.isOwn } : {}),
    // participantId is intentionally retained only by the local demo adapter.
    ...(includeDemoData && question.participantId ? { participantId: question.participantId } : {}),
    ...overrides,
  }
}

function applyStageSnapshot(snapshot) {
  const current = snapshot.currentQuestion ?? snapshot.current
  const upcoming = snapshot.upcomingQuestions ?? snapshot.upcoming ?? []
  if (current !== undefined || Array.isArray(upcoming)) {
    const stagedIds = new Set()
    const staged = []
    if (current) {
      const item = normalizeQuestion(current, { status: 'current', queuePosition: null })
      staged.push(item)
      stagedIds.add(item.id)
    }
    for (let index = 0; index < (upcoming || []).length; index += 1) {
      const item = normalizeQuestion(upcoming[index], { status: 'queued', queuePosition: index + 1 })
      if (!stagedIds.has(item.id)) staged.push(item)
      stagedIds.add(item.id)
    }
    const remaining = state.questions.filter((question) => !stagedIds.has(question.id) && !['current', 'queued'].includes(question.status))
    state.questions.splice(0, state.questions.length, ...staged, ...remaining)
  }

  const speaker = snapshot.currentSpeaker
  if (speaker) {
    const id = speaker.id || speaker.speakerId
    state.currentSpeakerId = id
    if (!state.speakers.some((candidate) => candidate.id === id)) {
      state.speakers.push({ id, name: speaker.name, handRaised: false, raisedAt: null, status: 'speaking' })
    }
  } else if (speaker === null) {
    state.currentSpeakerId = null
  }
  if (snapshot.questionsOpen !== undefined) state.session.questionsOpen = Boolean(snapshot.questionsOpen)
  if (snapshot.wallShowJoinDetails !== undefined) state.session.wallShowJoinDetails = Boolean(snapshot.wallShowJoinDetails)
  if (snapshot.sessionState) state.session.status = String(snapshot.sessionState).toLowerCase()
}

function applySnapshot(snapshot) {
  if (!snapshot) return
  snapshot = snapshot.snapshot || snapshot

  if (snapshot.session) Object.assign(state.session, snapshot.session)
  if (snapshot.code) state.session.code = snapshot.code
  if (snapshot.name) state.session.name = snapshot.name
  if ('subtitle' in snapshot) state.session.subtitle = String(snapshot.subtitle || '')
  if (snapshot.status) {
    state.session.status = String(snapshot.status).toLowerCase()
    state.session.questionsOpen = state.session.status === 'open'
  }
  if (snapshot.questionsOpen !== undefined) state.session.questionsOpen = Boolean(snapshot.questionsOpen)
  if (snapshot.requireName !== undefined) state.session.requireName = Boolean(snapshot.requireName)
  if (snapshot.questionIdentityMode !== undefined) state.session.questionIdentityMode = snapshot.questionIdentityMode
  if (snapshot.requireEmail !== undefined) state.session.requireEmail = Boolean(snapshot.requireEmail)
  if (snapshot.speakerViewEnabled !== undefined) state.session.speakerViewEnabled = Boolean(snapshot.speakerViewEnabled)
  if (snapshot.speakerPasswordRequired !== undefined) state.session.speakerPasswordRequired = Boolean(snapshot.speakerPasswordRequired)
  if (snapshot.defaultLanguage) state.session.locale = String(snapshot.defaultLanguage).toLowerCase()
  if (snapshot.participantAccess && typeof snapshot.participantAccess === 'object') {
    const access = snapshot.participantAccess
    if (access.participantId) state.participantId = String(access.participantId)
    state.participantMuted = Boolean(access.muted)
    state.accessBlocked = Boolean(access.banned)
    if (state.participantCode) {
      const mutedKey = `gather-participant-muted:${state.participantCode}`
      if (state.participantMuted) sessionStorage.setItem(mutedKey, 'true')
      else sessionStorage.removeItem(mutedKey)
    }
    if (state.accessBlocked) blockParticipantAccess()
  }
  if ('branding' in snapshot) state.session.branding = normalizeSessionBranding(snapshot)
  else if (snapshot.session && 'branding' in snapshot.session) state.session.branding = normalizeSessionBranding(snapshot.session)

  const settings = snapshot.settings
  if (settings) {
    const mode = String(settings.mode || '').toLowerCase()
    if (modeNames[mode]) state.session.audienceMode = modeNames[mode]
    if (Number.isFinite(settings.questionSlowModeSeconds)) state.session.questionSlowModeSeconds = settings.questionSlowModeSeconds
    if (Number.isFinite(settings.voteToggleLimitPerSecond)) state.session.rapidVoteLimit = settings.voteToggleLimitPerSecond
    if (Number.isFinite(settings.voteThrottleSeconds)) state.session.rapidVoteCooldownSeconds = settings.voteThrottleSeconds
    if (Number.isFinite(settings.publicQuestionLimit)) state.session.publicQuestionLimit = settings.publicQuestionLimit
    if (Number.isFinite(settings.expectedPeakConnections)) state.session.expectedPeak = settings.expectedPeakConnections
    if (settings.defaultLanguage) state.session.locale = String(settings.defaultLanguage).toLowerCase()
    // Realtime session-control notifications are sparse patches. Presence is
    // authoritative; an omitted boolean must not be mistaken for `false` (or,
    // for queue visibility, for `true`). Full bootstrap snapshots still carry
    // every setting and follow the same path.
    if ('requireName' in settings) state.session.requireName = Boolean(settings.requireName)
    if ('questionIdentityMode' in settings) state.session.questionIdentityMode = settings.questionIdentityMode
    if ('wallShowJoinDetails' in settings) state.session.wallShowJoinDetails = Boolean(settings.wallShowJoinDetails)
    if ('requireEmail' in settings) state.session.requireEmail = Boolean(settings.requireEmail)
    if ('speakerViewEnabled' in settings) state.session.speakerViewEnabled = Boolean(settings.speakerViewEnabled)
    if ('speakerPasswordRequired' in settings) state.session.speakerPasswordRequired = Boolean(settings.speakerPasswordRequired)
    if ('showQueuePositionToSpeakers' in settings) {
      state.session.showQueuePositionToSpeakers = Boolean(settings.showQueuePositionToSpeakers)
    }
  }

  if ('theme' in snapshot) {
    state.session.theme = snapshot.theme && typeof snapshot.theme === 'object' ? { ...snapshot.theme } : {}
  }
  if (Array.isArray(snapshot.questions)) {
    const priorQuestions = new Map(state.questions.map(question => [question.id, question]))
    state.questions.splice(0, state.questions.length, ...snapshot.questions.map((question) => normalizeQuestion(question, {}, priorQuestions)))
  }

  if (Array.isArray(snapshot.moderatorPolls)) applyLibrarySnapshot(state, snapshot.moderatorPolls, snapshot.pollResults, snapshot.revision)
  const definitions = snapshot.moderatorPolls || snapshot.polls
  const explicitSelection = definitions?.some(poll => typeof poll.isActive === 'boolean')
  const pollDefinition = snapshot.poll || (explicitSelection ? definitions.find(poll => poll.isActive === true)
    : definitions?.find((poll) => poll.isOpen || String(poll.status).toLowerCase() === 'open') || definitions?.[0])
    if (Array.isArray(definitions) || pollDefinition) rememberCurrentPollResponse(state)
    if (Array.isArray(definitions) && !pollDefinition) state.poll = emptyActivePoll()
  if (Array.isArray(definitions)) state.activePollRevision = Math.max(Number(state.activePollRevision) || 0, Number(snapshot.revision ?? pollDefinition?.revision) || 0)
  if (pollDefinition) {
      const pollId = pollDefinition.id || pollDefinition.pollId
      const rememberedResponse = readPollResponse(state, pollId)
      const hasPrivateResponse = Array.isArray(pollDefinition.selectedOptionIds) || typeof pollDefinition.responseText === 'string'
    const published = pollResultsPublished(pollDefinition)
    const aggregate = snapshot.pollResults?.[pollId] || (published ? demoCloudResults.get(demoCloudKey(pollId)) : null)
    const selectedOptionIds = Array.isArray(pollDefinition.selectedOptionIds)
      ? pollDefinition.selectedOptionIds.map(String)
        : state.poll.id === pollId
          ? [...(state.poll.selectedOptionIds || [])]
          : rememberedResponse?.selectedOptionIds || []
    const options = (pollDefinition.options || []).map((option) => ({
      id: option.id || option.optionId,
      label: option.label,
      votes: Number(option.votes ?? aggregate?.counts?.[option.id || option.optionId] ?? 0),
    }))
    Object.assign(state.poll, {
      id: pollId,
      isActive: pollDefinition.isActive,
      title: pollDefinition.title || pollDefinition.prompt || '',
      status: pollDefinition.status || (pollDefinition.isOpen ? 'open' : 'closed'),
      multiple: pollDefinition.multiple ?? String(pollDefinition.kind).toLowerCase() === 'multiplechoice',
      kind: pollDefinition.kind || (pollDefinition.multiple ? 'MultipleChoice' : 'SingleChoice'),
      resultsVisible: published,
      resultsAvailable: published && (Boolean(aggregate) || (state.poll.id === pollId && state.poll.resultsAvailable)),
        responded: pollDefinition.responded ?? (selectedOptionIds.length > 0 || Boolean(pollDefinition.responseText) || (!hasPrivateResponse && Boolean(rememberedResponse?.responded))),
        responseText: hasPrivateResponse ? String(pollDefinition.responseText || '') : (state.poll.id === pollId ? state.poll.responseText : rememberedResponse?.responseText) || '',
      wordCloud: published ? (aggregate?.wordCloud ?? pollDefinition.wordCloud ?? (state.poll.id === pollId ? state.poll.wordCloud : []) ?? []) : [],
      selectedOptionIds,
      totalResponses: Number(pollDefinition.totalResponses ?? aggregate?.responseCount ?? 0),
      revision: Number(pollDefinition.revision ?? (state.poll.id === pollId ? state.poll.revision : 0)),
      resultsRevision: Number(aggregate?.revision ?? (state.poll.id === pollId ? state.poll.resultsRevision : 0)),
        options,
      })
      if (hasPrivateResponse) rememberPollResponse(state, pollId, state.poll)
  }

  if (snapshot.stage) applyStageSnapshot(snapshot.stage)
  if ('currentQuestion' in snapshot || 'upcomingQuestions' in snapshot) applyStageSnapshot(snapshot)
  if (Number.isFinite(snapshot.revision)) {
    state.revision = Math.max(Number(state.revision) || 0, Number(snapshot.revision))
  }
}

function applyModeratorData({ questions, participants, speakers, hands } = {}) {
  if (Array.isArray(questions)) {
    // One lookup per existing question, rather than rescanning the reactive
    // array for every item in a reconnect snapshot.
    const priorQuestions = new Map(state.questions.map(question => [question.id, question]))
    state.questions.splice(0, state.questions.length, ...questions.map((question) => ({
      ...normalizeQuestion(question, {}, priorQuestions),
      participantId: question.participantId,
      queuePosition: question.queuePosition,
      answerNotes: question.answerNotes,
      answerText: question.answerText,
    })))
    if (!Array.isArray(participants) && !state.participantPage) {
      const existing = new Map(state.participants.map((participant) => [participant.id, participant]))
      const questionCounts = new Map()
      for (const question of questions) {
        if (question.participantId) questionCounts.set(question.participantId, (questionCounts.get(question.participantId) || 0) + 1)
      }
      const seen = new Set()
      const derived = questions
        .filter((question) => question.participantId && !seen.has(question.participantId) && seen.add(question.participantId))
        .map((question) => {
          const prior = existing.get(question.participantId)
          return {
            id: question.participantId,
            name: question.authorName?.trim() || prior?.name || `Anonymous · ${question.participantId.slice(-4).toUpperCase()}`,
            activity: `${questionCounts.get(question.participantId)} question(s)`,
            state: prior?.state || 'active',
            createdAt: prior?.createdAt,
          }
        })
      const withoutQuestions = state.participants.filter((participant) => !seen.has(participant.id))
      state.participants.splice(0, state.participants.length, ...derived, ...withoutQuestions)
    }
  }

  if (Array.isArray(participants)) {
    const questionCounts = new Map()
    for (const question of state.questions) {
      if (question.participantId) questionCounts.set(question.participantId, (questionCounts.get(question.participantId) || 0) + 1)
    }
    state.participants.splice(0, state.participants.length, ...participants.map((participant) => {
      const id = participant.id || participant.participantId
      const questionCount = questionCounts.get(id) || 0
      return {
        id,
        name: participant.name?.trim() || participant.displayName?.trim() || `Anonymous · ${String(id).slice(-4).toUpperCase()}`,
        activity: participant.activity || `${questionCount} question(s)`,
        state: participant.banned || participant.isBanned ? 'banned' : participant.muted || participant.isMuted ? 'muted' : 'active',
        createdAt: timestamp(participant.createdAt),
        thirdPartyId: participant.thirdPartyId || '',
      }
    }))
  }

  if (Array.isArray(speakers)) {
    const handBySpeaker = new Map((hands || []).map((hand) => [hand.speakerId, hand]))
    state.speakers.splice(0, state.speakers.length, ...speakers.map((speaker) => {
      const id = speaker.id || speaker.speakerId
      const hand = handBySpeaker.get(id)
      return {
        id,
        name: speaker.name,
        handRaised: Boolean(hand),
        raisedAt: hand ? timestamp(hand.raisedAt) : null,
        queuePosition: hand?.queuePosition,
        isPresent: speaker.isPresent !== false,
        isBlocked: Boolean(speaker.isBlocked),
        status: speaker.isBlocked ? 'blocked' : hand ? 'waiting' : (speaker.isPresent === false ? 'away' : 'joined'),
      }
    }))
  }
}

function toast(message, tone = 'success') {
  const item = { id: `toast-${Date.now()}-${Math.random()}`, message, tone }
  state.toasts.push(item)
  window.setTimeout(() => {
    const index = state.toasts.findIndex((candidate) => candidate.id === item.id)
    if (index >= 0) state.toasts.splice(index, 1)
  }, 4200)
}

function blockParticipantAccess() {
  state.accessBlocked = true
  state.participantMuted = true
  state.questions.splice(0, state.questions.length)
  state.poll = { ...state.poll, id: '', title: '', status: 'closed', responded: false, selectedOptionIds: [], resultsAvailable: false, totalResponses: 0, options: [] }
  state.speakers.splice(0, state.speakers.length)
  state.currentSpeakerId = null
  state.onDeckSpeakerId = null
  if (state.participantCode) {
    sessionStorage.removeItem(`gather-participant-token:${state.participantCode}`)
    sessionStorage.removeItem(`gather-participant-id:${state.participantCode}`)
    sessionStorage.removeItem(`gather-participant-muted:${state.participantCode}`)
  }
  state.participantToken = null
  state.participantCode = null
  state.connection = 'blocked'
}

function submitQuestion({ text, author = 'Anonymous' }) {
  if (!state.session.questionsOpen) throw new Error('Questions are closed.')
  if (participantNameRequired(state.session) && !state.participantIdentity.name.trim()) throw new Error('This event requires your name.')
  const wait = questionWaitSeconds()
  if (wait > 0) throw new Error(`Please wait ${wait} seconds.`)
  const question = {
    id: `q-${Date.now()}`,
    text: text.trim(),
    author: questionIdentityMode(state.session) === 'Anonymous' ? 'Anonymous' : author.trim() || 'Anonymous',
    participantId: state.participantId,
    isOwn: true,
    votes: 0,
    status: 'submitted',
    createdAt: Date.now(),
    edited: false,
    voted: false,
  }
  state.questions.unshift(question)
  state.lastQuestionAt = Date.now()
  state.nextQuestionAllowedAt = state.lastQuestionAt + state.session.questionSlowModeSeconds * 1000
  bumpRevision()
  toast('Your question was sent to the moderators.')
  return { ...question, nextQuestionAllowedAt: new Date(state.nextQuestionAllowedAt).toISOString() }
}

function issueParticipant(identity = {}) {
  state.participantIdentity = {
    name: identity.name?.trim() || '',
    email: identity.email?.trim() || '',
    thirdPartyId: identity.thirdPartyId?.trim() || '',
  }
  state.participantToken = `demo_participant_${state.session.id}_${crypto.randomUUID()}`
  state.participantCode = state.session.code.toUpperCase()
  sessionStorage.setItem(`gather-participant-token:${state.session.code.toUpperCase()}`, state.participantToken)
  return {
    token: state.participantToken,
    participantId: state.participantId,
    nextQuestionAllowedAt: state.nextQuestionAllowedAt ? new Date(state.nextQuestionAllowedAt).toISOString() : null,
  }
}

function editQuestion(id, text) {
  const question = state.questions.find((q) => q.id === id)
  if (!question) throw new Error('Question not found.')
  if (!(question.isOwn ?? question.participantId === state.participantId)) throw new Error('You cannot edit this question.')
  question.text = text.trim()
  question.edited = true
  bumpRevision()
  toast('Question updated.')
  return question
}

function questionWaitSeconds(at = Date.now()) {
  if (!state.lastQuestionAt || !state.session.questionSlowModeSeconds) return 0
  const readyAt = state.lastQuestionAt + state.session.questionSlowModeSeconds * 1000
  return Math.max(0, Math.ceil((readyAt - at) / 1000))
}

function voteWaitSeconds(at = Date.now()) {
  return Math.max(0, Math.ceil((state.voteThrottledUntil - at) / 1000))
}

function toggleVote(id, at = Date.now()) {
  if (state.session.audienceMode !== 'view-and-vote') throw new Error('Voting is disabled.')
  const existingWait = voteWaitSeconds(at)
  if (existingWait > 0) {
    const error = new Error(`Voting paused for ${existingWait} seconds.`)
    error.status = 429
    error.retryAfter = existingWait
    throw error
  }

  const windowStart = at - state.session.rapidVoteWindowSeconds * 1000
  state.voteEvents = state.voteEvents.filter((timestamp) => timestamp >= windowStart)
  state.voteEvents.push(at)
  if (state.voteEvents.length > state.session.rapidVoteLimit) {
    state.voteThrottledUntil = at + state.session.rapidVoteCooldownSeconds * 1000
    state.voteEvents = []
    toast('Rapid voting detected. Voting has been paused briefly.', 'warning')
    const error = new Error(`Voting paused for ${state.session.rapidVoteCooldownSeconds} seconds.`)
    error.status = 429
    error.retryAfter = state.session.rapidVoteCooldownSeconds
    throw error
  }

  const question = state.questions.find((q) => q.id === id)
  if (!question) throw new Error('Question not found.')
  question.voted = !question.voted
  question.votes = Math.max(0, question.votes + (question.voted ? 1 : -1))
  bumpRevision()
  return question
}

function setQuestionStatus(id, status) {
  const question = state.questions.find((q) => q.id === id)
  if (!question) return
  if (status === 'current') {
    state.questions.forEach((item) => {
      if (item.status === 'current') item.status = 'answered'
    })
  }
  question.status = status
  bumpRevision()
  toast(status === 'current' ? 'Question is live on the wall.' : `Question marked ${status}.`)
}

function moveQueued(id, direction) {
  const queue = state.questions.filter((q) => q.status === 'queued')
  const currentIndex = queue.findIndex((q) => q.id === id)
  const target = currentIndex + direction
  if (currentIndex < 0 || target < 0 || target >= queue.length) return
  const a = state.questions.indexOf(queue[currentIndex])
  const b = state.questions.indexOf(queue[target])
  ;[state.questions[a], state.questions[b]] = [state.questions[b], state.questions[a]]
  bumpRevision()
}

function toggleParticipantState(id, nextState) {
  const participant = state.participants.find((person) => person.id === id)
  if (!participant) return
  participant.state = nextState
  toast(`${participant.name} is now ${nextState}.`, nextState === 'active' ? 'success' : 'warning')
}

function respondToPoll(optionIds, text = null) {
  if (state.poll.responded) return
  const selected = Array.isArray(optionIds) ? optionIds : [optionIds]
  const valid = state.poll.options.filter((candidate) => selected.includes(candidate.id))
  if (!valid.length && !text?.trim()) return { accepted: false }
  valid.forEach((option) => { option.votes += 1 })
  state.poll.totalResponses += 1
  state.poll.responded = true
  state.poll.selectedOptionIds = valid.map((option) => option.id)
  if (text?.trim()) {
    state.poll.responseText = text.trim()
    const counts = new Map((demoCloudResults.get(demoCloudKey(state.poll.id))?.wordCloud || state.poll.wordCloud || []).map(word => [word.text, word.count]))
    for (const word of new Set(text.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [])) counts.set(word, (counts.get(word) || 0) + 1)
    const wordCloud = [...counts].map(([text, count]) => ({ text, count }))
    demoCloudResults.set(demoCloudKey(state.poll.id), { counts: {}, responseCount: state.poll.totalResponses, wordCloud })
    state.poll.wordCloud = pollResultsPublished(state.poll) ? wordCloud : []
    state.poll.resultsAvailable = pollResultsPublished(state.poll)
  }
  bumpRevision()
  toast('Poll response received.')
  return { accepted: true, selectedOptionIds: [...state.poll.selectedOptionIds], responseText: state.poll.responseText }
}

function toggleHand(speaker) {
  speaker.handRaised = !speaker.handRaised
  speaker.raisedAt = speaker.handRaised ? Date.now() : null
  speaker.status = speaker.handRaised ? 'waiting' : 'joined'
  bumpRevision()
}

function joinSpeaker(name) {
  const speaker = { id: `sp-${Date.now()}`, name: name.trim(), handRaised: false, raisedAt: null, status: 'joined' }
  state.speakers.push(speaker)
  state.activeSpeakerId = speaker.id
  state.speakerToken = `demo_speaker_${crypto.randomUUID()}`
  toast(`Welcome to the speaker room, ${speaker.name}.`)
  return { speaker, token: state.speakerToken }
}

function selectSpeaker(id) {
  state.currentSpeakerId = id
  const speaker = state.speakers.find((candidate) => candidate.id === id)
  if (speaker) {
    speaker.handRaised = false
    speaker.status = 'speaking'
    toast(`${speaker.name} is now on deck.`)
  }
  bumpRevision()
}

function updateCapacity(units, expiresAt = null) {
  const normalizedUnits = Number(units)
  Object.assign(state.platformCapacity, {
    desiredUnits: normalizedUnits,
    actualUnits: normalizedUnits,
    state: normalizedUnits > 0 ? 'Applied' : 'Released',
    expiresAt: normalizedUnits > 0 ? expiresAt : null,
    lastChangedBy: 'demo-platform-admin',
    updatedAt: new Date().toISOString(),
    etag: `demo-capacity-${Date.now()}`,
  })
  toast(`Application realtime capacity is now ${normalizedUnits} unit${normalizedUnits === 1 ? '' : 's'}.`)
  return { ...state.platformCapacity }
}

function createExport(format) {
  const item = { id: `ex-${Date.now()}`, format, createdAt: 'Just now', size: 'Preparing…', status: 'Preparing', expires: 'in 24 hours' }
  state.exports.unshift(item)
  toast(`${format} export is being prepared.`)
  window.setTimeout(() => {
    item.status = 'Ready'
    item.size = format === 'Word' ? '861 KB' : '4.1 MB'
  }, 1200)
  return item
}

function createApiKey(name, scopes, expiresAt) {
  const secret = `ns_live_${Math.random().toString(36).slice(2, 10)}.${crypto.randomUUID().replaceAll('-', '')}`
  const key = { id: `key-${Date.now()}`, name, prefix: `${secret.slice(0, 16)}••••••••`, scopes, lastUsed: 'Never', expires: new Date(expiresAt).toLocaleDateString(localeTag()), status: 'Active' }
  state.apiKeys.unshift(key)
  toast('API key created. Copy it now—it will not be shown again.')
  return { key, secret }
}

function revokeApiKey(id) {
  const key = state.apiKeys.find((candidate) => candidate.id === id)
  if (key) key.status = 'Revoked'
  toast('API key revoked.', 'warning')
}

function addWebhook({ name, url, events }) {
  const item = { id: `wh-${Date.now()}`, name, url, events, status: 'Pending test', lastDelivery: 'Never' }
  state.webhooks.unshift(item)
  toast('Webhook saved. A signed test delivery was queued.')
  return item
}

function authenticateModerator(passphrase) {
  if (!passphrase.trim()) throw new Error('Enter the event passphrase.')
  state.moderatorAuthenticated = true
  state.moderatorToken = `demo_moderator_${crypto.randomUUID()}`
  toast('Moderator access granted.')
  return { accessToken: state.moderatorToken, expiresIn: 3600 }
}

function exchangeModeratorMagicLink(grantId, secret) {
  if (!grantId || !secret) throw new Error('This moderator link is invalid.')
  state.moderatorAuthenticated = true
  state.moderatorToken = `demo_moderator_${crypto.randomUUID()}`
  toast('Private moderator link accepted.')
  return { accessToken: state.moderatorToken, expiresIn: 3600 }
}

function resetDemo() {
  clearOwnPollResponses(state)
  demoCloudResults.clear()
  const replacement = initialState()
  Object.keys(state).forEach((key) => delete state[key])
  Object.assign(state, replacement)
}

export const demoStore = {
  state,
  currentQuestion,
  queuedQuestions,
  waitingSpeakers,
  submitQuestion,
  issueParticipant,
  editQuestion,
  questionWaitSeconds,
  voteWaitSeconds,
  toggleVote,
  setQuestionStatus,
  moveQueued,
  toggleParticipantState,
  respondToPoll,
  toggleHand,
  joinSpeaker,
  selectSpeaker,
  updateCapacity,
  createExport,
  createApiKey,
  revokeApiKey,
  addWebhook,
  authenticateModerator,
  exchangeModeratorMagicLink,
  toast,
  resetDemo,
  applySnapshot,
  applyModeratorData,
  blockParticipantAccess,
}

export function useDemoStore() {
  return demoStore
}
